import { createAction, createSlice } from "@reduxjs/toolkit";
import userService from "../service/user.service";
import authService from "../service/auth.service";
import localStorageService from "../service/http.localStorage";
import history from "../utils/history";
import { generateAuthError } from "../utils/generateAuthError";
import { asList, readContent, replaceById } from "../utils/readContent";

const initialState = localStorageService.getAccessToken()
  ? {
      entities: null,
      isLoading: true,
      error: null,
      auth: { userId: localStorageService.getUserId() },
      isLoggedIn: true,
      dataLoaded: false,
      isVerify: false,
    }
  : {
      entities: null,
      isLoading: false,
      error: null,
      auth: null,
      isLoggedIn: false,
      dataLoaded: false,
      isVerify: false,
    };

const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    usersRequested: (state) => {
      state.isLoading = true;
    },
    usersReceived: (state, action) => {
      state.entities = action.payload;
      state.dataLoaded = true;
      state.isLoading = false;
    },
    usersRequestFailed: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    authRequestSuccess: (state, action) => {
      state.auth = action.payload;
      state.isLoggedIn = true;
    },
    authRequestFailed: (state, action) => {
      state.isLoggedIn = false;
      state.error = action.payload;
    },
    verifyRequestSend: (state) => {
      state.error = null;
      state.isLoading = false;
    },
    verifyRequestReceive: (state, action) => {
      state.isVerify = action.payload;
      state.isLoading = false;
    },
    verifyRequestFailed: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    userCreated: (state, action) => {
      if (!Array.isArray(state.entities)) {
        state.entities = [];
      }
      state.entities.push(action.payload);
    },
    userLoggedOut: (state) => {
      state.entities = null;
      state.isLoggedIn = false;
      state.isVerify = false;
      state.auth = null;
      state.dataLoaded = false;
    },
    userUpdateSuccessed: (state, action) => {
      state.entities = replaceById(state.entities, action.payload);
      state.isLoading = false;
    },
    authRequested: (state) => {
      state.error = null;
    },
  },
});

const { reducer: usersReducer, actions } = usersSlice;
const {
  usersRequested,
  usersReceived,
  usersRequestFailed,
  authRequestSuccess,
  authRequestFailed,
  userLoggedOut,
  userUpdateSuccessed,
  verifyRequestSend,
  verifyRequestFailed,
} = actions;

const authRequested = createAction("users/authRequested");
const userUpdateRequested = createAction("users/userUpdateRequested");
const userUpdateFailed = createAction("users/userUpdateFailed");

export const login =
  ({ payload, redirect }) =>
  async (dispatch) => {
    const { email, password } = payload;
    dispatch(authRequested());
    try {
      const data = await authService.login({ email, password });
      localStorageService.setTokens(data);
      dispatch(authRequestSuccess({ userId: data.userId }));
      // history.push(redirect);
    } catch (error) {
      const { code, message } = error.response.data.error;
      if (code === 400) {
        const errorMessage = generateAuthError(message);
        dispatch(authRequestFailed(errorMessage));
      } else {
        dispatch(authRequestFailed(error.message));
      }
    }
  };

export const signUp = (payload) => async (dispatch) => {
  dispatch(authRequested());
  try {
    const data = await authService.register(payload);
    localStorageService.setTokens(data);
    dispatch(authRequestSuccess({ userId: data.userId }));
    return data;
  } catch (error) {
    dispatch(
      authRequestFailed(
        error?.response?.data?.error?.message ||
          error?.message ||
          "AUTH_REGISTER_FAIL"
      )
    );
    return null;
  }
};

export const logOut = () => async (dispatch, getState) => {
  const { entities, auth } = getState().users;
  const user = entities.find((u) => u._id === auth.userId);
  await userService.update({ ...user, isVerify: false });
  localStorageService.removeAuthData();
  dispatch(userLoggedOut());
  history.push("/");
};

export const verifySend = () => async (dispatch) => {
  dispatch(usersRequested());
  try {
    await userService.sendVerify();
    dispatch(verifyRequestSend());
  } catch (error) {
    dispatch(verifyRequestFailed(error.message));
  }
};
export const removeError = () => async (dispatch) => {
  dispatch(authRequestFailed(null));
};

export const Verify =
  ({ code }) =>
  async (dispatch) => {
    dispatch(usersRequested());
    try {
      const content = readContent(await userService.verify(code));
      if (content && content._id != null) {
        dispatch(userUpdateSuccessed(content));
      }
      // history.push(redirect);
    } catch (error) {
      dispatch(verifyRequestFailed(error.message));
    }
  };

export const loadUsersList = () => async (dispatch) => {
  dispatch(usersRequested());
  try {
    dispatch(usersReceived(asList(await userService.get())));
  } catch (error) {
    dispatch(usersRequestFailed(error.message));
  }
};

export const updateUser = (payload) => async (dispatch) => {
  dispatch(userUpdateRequested());
  try {
    const content = readContent(await userService.update(payload));
    if (content && content._id != null) {
      dispatch(userUpdateSuccessed(content));
    }
    return content || null;
  } catch (error) {
    dispatch(userUpdateFailed(error.message));
  }
};

export const getUsersList = () => (state) => state.users.entities;

export const getCurrentUserData = () => (state) => {
  return state.users.entities
    ? state.users.entities.find((u) => u._id === state.users.auth.userId)
    : null;
};

export const getUserById = (userId) => (state) => {
  if (state.users.entities) {
    return state.users.entities.find((u) => u._id === userId);
  }
};

export const findUserByEmail = (id) => (state) => {
  if (state.users?.entities) {
    return state.users.entities.find((u) => u._id === id);
  }
};

export const getIsLoggedIn = () => (state) => state.users.isLoggedIn;
export const getDataStatus = () => (state) => state.users.dataLoaded;
export const getUsersLoadingStatus = () => (state) => state.users.isLoading;
export const getCurrentUserId = () => (state) => state.users.auth.userId;
export const getAuthErrors = () => (state) => state.users.error;
export const getIsVerifyUser = () => (state) => state.users.isVerify;

export default usersReducer;
